export const DEFAULT_CZ = '3C';

export enum EvType {
    sedan = 'sedan',
    truck = 'truck',
    phev = 'phev',
}

export const AllEvTypes = Object.values(EvType).filter(
    (v) => typeof v === 'string'
);

export const EvToDisplayTypes = {
    [EvType.sedan]: 'Sedan',
    [EvType.truck]: 'Truck',
    [EvType.phev]: 'Plug-in Hybrid',
};

export type SingleEvDict = {
    ev_type: EvType;
    miles: number;
    elec_pct: number;
    home_chg_pct: number;
};

export const AllDisplayEvTypes = Object.values(EvToDisplayTypes);

export const DisplayToEvTypes = Object.fromEntries(
    Object.entries(EvToDisplayTypes).map(([k, v]) => [v, k])
) as { [k: string]: EvType };

export const UtilityCompanyOther = 'Other';

export enum Occupancy {
    Vacant = 'Vacant',
    Occupied = 'Occupied',
}

export const AllOccupancies = Object.values(Occupancy).filter(
    (v) => typeof v === 'string'
);

export enum HomeType {
    SingleFamilyDetached = 'Single-Family Detached',
    SingleFamilyAttached = 'Single-Family Attached',
    MultiFamilyWith5PlusUnits = 'Multi-Family with 5+ Units',
    MultiFamilyWith2To4Units = 'Multi-Family with 2 - 4 Units',
    MobileHome = 'Mobile Home',
}

export const AllHomeTypes = Object.values(HomeType).filter(
    (v) => typeof v === 'string'
);

export type CalculateAllResult = {
    ev: number;
    home: number;
    total_added: number;
    new_total: number;
    recommended_offset: number;
};

export type FullEnergyUsageInput = {
    occupancy: string;
    zip: string;
    sqft: number;
    yr_built: number;
    hh_size: number;
    electric_heat: boolean;
    has_heatpump: boolean;
    input_usage: number;
    has_ac: boolean;
    has_electric_stove: boolean;
    add_ev: SingleEvDict[];
    add_people: number;
    add_hvac_use: number;
    add_electrify: boolean;
    add_clean_heat_and_ac: boolean;
    add_heatpump_water_heater: boolean;
    add_induction_stove: boolean;
    stories: number;
};

// Most fields are optional, so use the partial type as the default.
export type EnergyUsageInput = Partial<FullEnergyUsageInput>;

export type EnergyUsageOutput = {
    input_usage: number;
    base_usage: number;
    added_usage: number;
    added_to_input: number;
    added_to_base: number;
    input_offset: number;
    base_offset: number;
    ev_total: number;
    evs: number[];
    home: number;
    stove?: number;
    hvac?: number;
    hvac_comfort?: number;
    hpwh?: number;
    backoff_count: number;
    hhSizeModificationImpactOnly?: boolean;
    hvacModificationPresent?: boolean;
    notes?: {
        hhSizeChangeNote?: string;
        hvacPresenceNote?: string;
    };
};

export const SAMPLE_SEDAN = {
    ev_type: EvType.sedan,
    miles: 12000,
    elec_pct: 1,
    home_chg_pct: 0.8,
} satisfies SingleEvDict;

export const SAMPLE_TRUCK = {
    ev_type: EvType.truck,
    miles: 15000,
    elec_pct: 1,
    home_chg_pct: 0.8,
} satisfies SingleEvDict;

export const SAMPLE_PHEV = {
    ev_type: EvType.phev,
    miles: 13000,
    elec_pct: 0.3,
    home_chg_pct: 1.0,
} satisfies SingleEvDict;

export const SAMPLE_INPUT_DICT = {
    input_usage: 8000,
    sqft: 2000,
    yr_built: 2020,
    hh_size: 5,
    electric_heat: false,
    has_heatpump: false,
    zip: '94501',
    occupancy: Occupancy.Occupied,
    has_ac: true,
    add_ev: [SAMPLE_SEDAN, SAMPLE_TRUCK, SAMPLE_PHEV],
    add_people: 2,
    add_hvac_use: 0.2,
    add_electrify: false,
    add_clean_heat_and_ac: false,
    add_heatpump_water_heater: false,
    add_induction_stove: false,
    stories: 1,
    has_electric_stove: false,
} satisfies FullEnergyUsageInput;
